"use client";

import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Label, Text } from "react-aria-components";
import {
  Controller,
  type ControllerProps,
  type DefaultValues,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type FormState,
  type UseFormProps,
  useForm,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { cx } from "@/utils/cx";

const Form = FormProvider;

// Context to suppress inline error messages when using toast-based validation
const SuppressInlineErrorsContext = React.createContext(false);

export const SuppressInlineErrors = ({ children }: { children: React.ReactNode }) => (
  <SuppressInlineErrorsContext.Provider value={true}>
    {children}
  </SuppressInlineErrorsContext.Provider>
);

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cx("grid gap-1", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

function FormLabel({
  className,
  required,
  children,
  ...props
}: React.ComponentProps<typeof Label> & { required?: boolean }) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-error={!!error}
      className={cx(
        "data-[error=true]:text-error-primary flex gap-1 tracking-wide font-semibold text-quaternary",
        className,
      )}
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {required && <span className="text-error-primary">*</span>}
    </Label>
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return (
    <Text
      slot="description"
      id={formDescriptionId}
      className={cx("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const suppressInlineErrors = React.useContext(SuppressInlineErrorsContext);
  const body = error ? String(error?.message ?? "") : props.children;

  // Don't show inline errors when using toast-based validation
  if (suppressInlineErrors && error) {
    return null;
  }

  if (!body) {
    return null;
  }

  return (
    <p
      slot="errorMessage"
      id={formMessageId}
      className={cx("text-error-primary text-sm", className)}
      {...props}
    >
      {body}
    </p>
  );
}

export type Result<T, E> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: E;
    };

export type SubmitHandler<
  T extends FieldValues = FieldValues,
  S = T,
  E = Error,
> = (
  data: T,
  formState?: {
    dirtyFields: FormState<T>["dirtyFields"];
    defaultValues: FormState<T>["defaultValues"];
  },
) => Promise<Result<S, E>>;
export type SuccessHandler<T> = (data: T) => Promise<void>;
export type ErrorHandler<E = Error> = (error: E) => Promise<void>;

export type UseHookFormProps<
  T extends FieldValues = FieldValues,
  S = T,
  E = Error,
> = Omit<UseFormProps<T>, "defaultValues"> & {
  onSubmit?: SubmitHandler<T, S, E>;
  onSuccess?: SuccessHandler<S>;
  onError?: ErrorHandler<E>;
  defaultErrors?: FieldErrors<T>;
  defaultValues?: DefaultValues<T>;
};

export const useHookForm = <
  T extends FieldValues = FieldValues,
  S = T,
  E = Error,
>({
  onSubmit,
  onSuccess,
  onError,
  defaultErrors,
  ...props
}: UseHookFormProps<T, S, E>) => {
  const [fatalError, setFatalError] = React.useState<unknown>(undefined);
  const [transition, startTransition] = React.useTransition();

  const form = useForm<T>({
    ...props,
  });

  const onFormValid = (values: T) => {
    startTransition(async () => {
      try {
        if (!onSubmit) {
          onSuccess?.(values as unknown as S);
          return;
        }

        const formState = {
          dirtyFields: form.formState.dirtyFields,
          defaultValues: form.formState.defaultValues,
        };

        const result = await onSubmit(values, formState);
        if (result?.success) {
          onSuccess?.(result.data);
        } else if (result?.error) {
          onError?.(result.error);
        }
      } catch (e) {
        setFatalError(e);
        throw e;
      }
    });
  };

  const handleSubmit = form.handleSubmit(onFormValid);

  return {
    form,
    handleSubmit,
    transition,
    fatalError,
  };
};

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
};
